/* eslint-disable react/display-name */
import React, { useContext } from 'react';
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	type GridColDef,
	type GridRowParams,
} from '@mui/x-data-grid-premium';
import { type GridInitialState, UnitsContext } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { SkeletonCell } from '../RangeDataGrid/SkeletonCell';
import { ActionCell } from './ActionCell';
import { StatusTagCell } from './StatusTagCell';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

export const getWidth = (
	field: string,
	dataGridState?: GridInitialState,
): number | undefined => {
	// Get the width of the column from the saved state if available
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field]
	) {
		return dataGridState.columns.dimensions[field].width;
	} else return undefined;
};

export const getFlex = (
	field: string,
	flex: number,
	dataGridState?: GridInitialState,
): number | undefined => {
	// If there is a saved width then return undefined
	// We dont want to specify both flex and width since flex will override the width
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field] &&
		dataGridState.columns.dimensions[field].width
	) {
		return undefined;
	} else return flex;
};

export const tableColumns = (
	countKey: string,
	onExcludeActions: (excluded: boolean, id?: number) => void,
	onGroupActions: (group: boolean, id?: number) => void,
) => {
	const { t } = useGlobalization();
	const { system } = useContext(UnitsContext);
	const lengthUnit =
		system === MEASUREMENT_SYSTEM.METRIC ? t('Kilometers') : t('Miles');

	const header = new Array<GridColDef>();

	if (t) {
		header.push(
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				sortable: false,
				align: 'center',
				filterable: false,
				hideable: false,
				maxWidth: 50,
				minWidth: 50,
				width: 50,
				disableColumnMenu: true,
			},
			{
				field: 'value',
				headerName: t('Values'),
				sortable: false,
				hideable: false,
				align: 'left',
				flex: 1.5,
				minWidth: 200,
			},
			{
				field: 'count',
				headerName: t('Asset Count'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				type: 'number',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'countPercent',
				headerName: t('Asset Count (%)'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'length',
				headerName: t('Length ({{unit}})', { unit: lengthUnit }),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				type: 'number',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'lengthPercent',
				headerName: t('Length (%)'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'excluded',
				headerName: t('Status'),
				sortable: true,
				filterable: true,
				flex: 0.5,
				minWidth: 110,
				align: 'left',
				type: 'singleSelect',
				valueOptions: [
					{
						value: false,
						label: t('Included'),
					},
					{ value: true, label: t('Excluded') },
				],
				renderCell: params => {
					return StatusTagCell(
						params.row['value'],
						params.value,
						'100%',
					);
				},
			},
			{
				field: 'menu',
				headerName: '',
				sortable: false,
				filterable: false,
				hideable: false,
				// flex: 0.5,
				maxWidth: 50,
				minWidth: 50,
				width: 50,
				align: 'center',
				disableColumnMenu: true,
				type: 'actions',
				getActions: (params: GridRowParams) => {
					if (params.row['value'] !== '--') {
						return ActionCell(
							params,
							countKey,
							onExcludeActions,
							onGroupActions,
						);
					} else {
						return [];
					}
				},
			},
		);
	}

	return header;
};
